import React from "react";
import classNames from "classnames";
import { Link as InertiaLink } from "@inertiajs/react";

interface PageHeaderProps {
  children: React.ReactNode;
  className?: string;
}

const PageHeader = ({ children, className }: PageHeaderProps) => {
  return <h1 className={classNames("page-header", className)}>{children}</h1>;
};

type SectionHeaderProps = {
  as?: "h1" | "h2" | "h3";
  children: React.ReactNode;
  large?: boolean;
  medium?: boolean;
  serif?: boolean;
  className?: string;
};

const SectionHeader = (props: SectionHeaderProps) => {
  const { as, children, large, medium, serif, className } = props;

  const elementClassName = classNames(
    {
      "section-header": true,
      "section-header--large": large,
      "section-header--medium": medium,
      "section-header--serif": serif,
    },
    className,
  );

  return as === "h1" ? (
    <h1 className={elementClassName}>{children}</h1>
  ) : as === "h2" ? (
    <h2 className={elementClassName}>{children}</h2>
  ) : (
    <h3 className={elementClassName}>{children}</h3>
  );
};

interface SectionTitleProps {
  children: React.ReactNode;
  medium?: boolean;
  large?: boolean;
  className?: string;
  title?: string;
}

const SectionTitle = ({ children, medium, large, className, title }: SectionTitleProps) => {
  const elementClassName = classNames(
    "section-title",
    { "section-title--medium": medium },
    { "section-title--large": large },
    className,
  );

  return (
    <h4 className={elementClassName} title={title}>
      {children}
    </h4>
  );
};

type BodyProps = {
  medium?: boolean;
  metadata?: boolean;
  strikethrough?: boolean;
  status?: "default" | "active";
  secondary?: boolean;
  light?: boolean;
  children?: React.ReactNode;
  as?: "p" | "span" | "div";
  error?: boolean;
  className?: string;
  style?: React.CSSProperties;
  dangerouslySetInnerHTML?: { __html: string };
};

const Body = (props: BodyProps) => {
  const {
    as,
    medium,
    metadata,
    strikethrough,
    status,
    secondary,
    children,
    light,
    error,
    className,
    style,
    ...rest
  } = props;

  const Element = as || "p";

  const elementClassName = classNames(
    {
      body: true,
      body__secondary: secondary,
      "body--light": light,
      "body--metadata": metadata,
      "body--strikethrough": strikethrough,
      "body--medium": medium,
      "body--active": status === "active",
      "body--error": error,
    },
    className,
  );

  return (
    <Element {...rest} style={style} className={elementClassName}>
      {children}
    </Element>
  );
};

type SecondaryBodyProps = {
  medium?: boolean;
  metadata?: boolean;
  strikethrough?: boolean;
  light?: boolean;
  children: React.ReactNode;
  className?: string;
};

const SecondaryBody = ({ children, className, ...props }: SecondaryBodyProps) => {
  return (
    <Body {...props} secondary={true} className={className}>
      {children}
    </Body>
  );
};

type LinkProps = {
  href: string;
  children: React.ReactNode;
  external?: boolean;
  ref?: React.RefObject<HTMLAnchorElement>;
  onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
  secondary?: boolean;
  medium?: boolean;
};

const Link = ({
  children,
  external = false,
  href,
  medium = false,
  ref,
  secondary = false,
  ...rest
}: LinkProps) => {
  const linkClassName = classNames("link", {
    link__secondary: secondary,
    "link--medium": medium,
  });

  const LinkElement = external ? "a" : InertiaLink;

  const target = external ? "_blank" : undefined;
  const rel = external ? "noopener noreferrer" : undefined;

  return (
    <LinkElement
      {...rest}
      href={href}
      target={target}
      rel={rel}
      className={linkClassName}
      ref={ref}
    >
      {children}
    </LinkElement>
  );
};

type LabelProps = {
  htmlFor: string;
  id: string;
  children: React.ReactNode;
  required?: boolean;
  className?: string;
};

const Label = (props: LabelProps) => {
  const className = classNames("label", props.className);

  return (
    <label id={props.id} htmlFor={props.htmlFor} className={className}>
      {props.children}
      {props.required && <span aria-hidden="true">*</span>}
    </label>
  );
};

const TableHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <p className={classNames("table-header", className)}>{children}</p>;
};

export { SectionHeader, Body, PageHeader, SecondaryBody, SectionTitle, Link, Label, TableHeader };
